import React, { useMemo } from "react";
import * as Icons from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from "recharts";
import { useApp } from "@/context/AppContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, SectionCard, InfoNote } from "@/components/uikit";
import { scoreAnomaly, CLASS_STYLES } from "@/lib/anomaly";

const DRIFT_MAG = { NONE: 0, WARNING: 1.6, DRIFT: 3.2 };

const chartTip = {
  contentStyle: { background: "#0D121D", border: "1px solid #1E293B", borderRadius: 8, fontSize: 12 },
  labelStyle: { color: "#94A3B8" },
};

export default function DriftMonitor() {
  const { records, integrity, settings, updateThresholds } = useApp();
  const th = settings.thresholds;

  const analysis = useMemo(() => {
    return records.map((r, i) => {
      const prev = records[i - 1];
      const tele = r.telemetry || {};
      const drift = tele.raw_drift != null ? Math.abs(tele.raw_drift - (tele.corrected_offset ?? 0)) : DRIFT_MAG[r.drift_status] ?? 0;
      const baselineDifference = tele.baseline_difference != null ? tele.baseline_difference : drift * 0.9;
      const temporalDelta = prev ? Math.min(400, Math.abs(new Date(r.timestamp) - new Date(prev.timestamp)) / 1000) : 0;
      const res = integrity[i];
      const integrityBroken = res ? !(res.selfOk && res.linkOk) : false;
      const scored = scoreAnomaly({ baselineDifference, temporalDelta, driftMagnitude: drift, integrityBroken }, th);
      return {
        index: i,
        event_id: r.event_id,
        type: r.payload_type,
        drift: Number(drift.toFixed(3)),
        score: scored.score,
        classification: scored.classification,
        explanation: scored.explanation,
        factors: scored.factors,
      };
    });
  }, [records, integrity, th]);

  const alerts = analysis.filter((a) => a.classification !== "NORMAL");

  return (
    <div>
      <PageHeader overline="Deterministic Anomaly Detection" title="Drift Monitor" icon="Waves"
        subtitle="Explainable anomaly scoring across the evidence chain. No AI or external API required." />

      <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <SectionCard title="Thresholds" icon="SlidersHorizontal" testId="drift-thresholds-card">
          <div className="space-y-3">
            {[["observe", "OBSERVE ≥"], ["warning", "WARNING ≥"], ["critical", "CRITICAL ≥"]].map(([key, label]) => (
              <div key={key}>
                <Label htmlFor={`th-${key}`} className="mb-1 block text-[11px] text-slate-400">{label}</Label>
                <Input id={`th-${key}`} data-testid={`drift-threshold-${key}`} type="number" min={0} max={100} value={th[key]}
                  onChange={(e) => updateThresholds({ ...th, [key]: Number(e.target.value) })}
                  className="h-9 border-slate-700 bg-slate-950 font-mono text-xs text-cyan-200" />
              </div>
            ))}
            <InfoNote tone="info">Score combines baseline difference, temporal deviation, drift magnitude, and integrity state.</InfoNote>
          </div>
        </SectionCard>

        <SectionCard title="Drift & Anomaly Across Chain" icon="Activity" className="lg:col-span-2" testId="drift-chart-card">
          {analysis.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500">No records to analyze.</p>
          ) : (
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <ComposedChart data={analysis}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                  <XAxis dataKey="index" stroke="#64748B" fontSize={11} />
                  <YAxis yAxisId="left" stroke="#64748B" fontSize={11} />
                  <YAxis yAxisId="right" orientation="right" stroke="#64748B" fontSize={11} domain={[0, 100]} />
                  <Tooltip {...chartTip} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <ReferenceLine yAxisId="right" y={th.critical} stroke="#FF3D00" strokeDasharray="4 4" />
                  <ReferenceLine yAxisId="right" y={th.warning} stroke="#FFB300" strokeDasharray="4 4" />
                  <Bar yAxisId="right" dataKey="score" fill="#FFB300" radius={[3, 3, 0, 0]} name="Anomaly score" />
                  <Line yAxisId="left" type="monotone" dataKey="drift" stroke="#00E5FF" strokeWidth={2} dot name="Drift magnitude" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </SectionCard>
      </div>

      <SectionCard title={`Alerts & Classifications (${alerts.length} non-normal)`} icon="AlertTriangle" testId="drift-alerts-card">
        {analysis.length === 0 ? (
          <p className="py-4 text-center text-sm text-slate-500">No records.</p>
        ) : (
          <div className="space-y-2">
            {analysis.map((a) => {
              const style = CLASS_STYLES[a.classification];
              const Icon = Icons[style.icon];
              return (
                <div key={a.event_id} className={`rounded-md border ${style.border} ${style.bg} p-3`} data-testid={`drift-row-${a.event_id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="flex items-center gap-2">
                      <Icon className={`h-4 w-4 ${style.text}`} aria-hidden="true" />
                      <span className="font-mono text-xs text-slate-300">#{a.index} · {a.type}</span>
                      <span className={`font-heading text-xs font-bold uppercase tracking-wide ${style.text}`}>{a.classification}</span>
                    </span>
                    <span className="font-mono text-xs text-slate-400">score {a.score}/100 · drift {a.drift}</span>
                  </div>
                  <p className="mt-1.5 text-xs leading-relaxed text-slate-400">{a.explanation}</p>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
